import React, {useEffect, useMemo, useRef} from 'react';
import {designSystemToken} from '@lightricks/react-design-system';
import styles from './TypeSelector.module.scss';

interface TypeProps {
  id: string;
  icon: React.ReactElement | string;
}

export interface TypeSelectorProps {
  testID?: string;
  types: TypeProps[];
  selectedTypeId: string;
  onChange: (id: string) => void;
  backgroundColor?: string;
  borderColor?: string;
  selectionColor?: string;
  containerStyle?: string;
}

function getActiveIndex(types: TypeProps[], selectedTypeId?: string) {
  return types.findIndex((type: TypeProps) => type.id === selectedTypeId);
}

function TypeSelector(props: TypeSelectorProps) {
  const {
    testID = 'type-selector',
    types,
    selectedTypeId,
    onChange,
    backgroundColor = designSystemToken('semantic.bg.secondary'),
    borderColor = designSystemToken('semantic.bg.neutral-inverse'),
    selectionColor = designSystemToken('semantic.bg.tertiary'),
    containerStyle,
  } = props;

  const controlRef = useRef<HTMLDivElement>(null);

  const activeIndex = useMemo(
    () => getActiveIndex(types, selectedTypeId),
    [types, selectedTypeId]
  );

  const typesWithRefs = useMemo(() => {
    return types.map((type) => ({
      ...type,
      ref: React.createRef<HTMLDivElement>(),
    }));
  }, [types]);

  useEffect(() => {
    if (activeIndex === -1) {
      return;
    }
    const activeTypeRef = typesWithRefs[activeIndex].ref;
    if (!activeTypeRef.current || !controlRef.current) {
      return;
    }
    const {style} = controlRef.current;

    style.setProperty('--background-color', backgroundColor);
    style.setProperty('--border-color', borderColor);
    style.setProperty('--selection-color', selectionColor);

    const activeEl = typesWithRefs[activeIndex].ref.current;
    if (!activeEl) {
      return;
    }

    style.setProperty('--highlight-width', `${activeEl.offsetWidth}px`);
    style.setProperty('--highlight-x-pos', `${activeEl.offsetLeft}px`);
  }, [
    activeIndex,
    types,
    typesWithRefs,
    backgroundColor,
    borderColor,
    selectionColor,
  ]);

  return (
    <div
      className={`${styles.container} ${containerStyle || ''}`}
      data-testid={testID}
      ref={controlRef}
    >
      <div
        className={`${styles.controls} ${
          controlRef.current ? styles.ready : ''
        }`}
      >
        {typesWithRefs?.map((typeItem) => (
          <div
            key={typeItem.id}
            className={`${styles.type} ${
              typeItem.id === selectedTypeId ? styles.active : ''
            }`}
            ref={typeItem.ref}
          >
            <div
              className={styles.button}
              onClick={() => onChange(typeItem.id)}
            >
              {typeof typeItem.icon === 'string' ? (
                <img className={styles.icon} src={typeItem.icon} alt="" />
              ) : (
                typeItem.icon
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TypeSelector;
